import { computed, watch } from 'vue'
import {
  setFeaturesInView as setFeaturesInViewState,
  setFeaturesHovered as setFeaturesHoveredState,
  setQueryFeatures as setQueryFeaturesState,
  setFeaturesFiltered as setFeaturesFilteredState,
  setFeaturesFavorited as setFeaturesFavoritedState,
} from '@/services/map-service'
import { getMapFeatures, FEATURE_DETECTION_PADDING } from '@/lib/feature-utils'
import MapFeature from '@/lib/map-feature'
import { captureException } from '@sentry/vue'
import MapElementWrapper from '@/components/map-x/map-element-wrapper.vue'
import { setHTMLElementToMapLocation } from '@/lib/map-x/anyhtml'
import { createHTMLElement } from '@/lib/map-x/create-html-element'
import { getMapEvents } from '@/composables/map-x/use-map-events'

let selectedFeature
let targetedFeature
let selectedSearchFeature
let hoveredFeatures = []
let mapViewerPopup
let markersSaved = []
let filteredFeatures = []
let favoritedFeatures = []
const watchers = []
let userMarker = null

/**
 * Cleans up registered watchers.
 */
export const unwatchFeatureWatchers = () => {
  while (watchers.length) {
    watchers.shift().call(this)
  }
}

/**
 * Iterate over features argument's keys to add a watcher for each source.
 * Watchers will trigger when either features are changed in the handler
 * or if the optionally specified trigger is updated.
 *
 * @typedef {object} FeatureValue
 * @property {function} handler - function to get features geojson
 * @property {function} trigger - trigger to manually fire watcher
 * @property {function} callback - callback for after watcher runs
 *
 * @param {object} map - Mapbox instance
 * @param {object<string, FeatureValue>} features
 */
export const setFeatureWatchers = (map, features, emit) => {
  for (const sourceKey in features) {
    const { handler, callback } = features[sourceKey]

    // use provided trigger or feature handler
    const trigger = features[sourceKey].trigger
      ? computed(features[sourceKey].trigger)
      : computed(handler)

    // store update function
    const updateFnc = () =>
      updateFeatures(map, sourceKey, handler(), emit, callback)

    // create watcher for updating features
    const watcher = watch(trigger, updateFnc, { immediate: true, deep: true })

    // store watcher for cleanup when map unmounts
    watchers.push(watcher)
  }
}

/**
 * Sets the feature state of the given id, source, and source layer to
 * the state passed in.  We want to use this function to highlight when
 * the feature is rendered on the map and we have the state object
 * setup in the stylesheet.  This function could also be used to set
 * other states of features assuming they are setup properly.
 *
 * @param {Mapbox} map
 * @param {Object} featureState - {
 *    id: (number| string),
 *    source: (string)
 *    sourceLayer: (string)
 *    state: (object e.g. { selected: true})
 *
 * }
 *
 */
export const setFeatureSelected = (map, featureState) => {
  // unselect the old feature
  if (selectedFeature) {
    // Features can be selected & filtered simultaneously
    // Primarily when hovering items in the discover list to highlight on map
    const filteredState = featureIsFiltered(map, selectedFeature)
    // This unselects the previous feature, while retaining it's filtered state
    map.setFeatureState(
      {
        id: selectedFeature.id,
        source: selectedFeature.source,
        sourceLayer: selectedFeature.sourceLayer,
      },
      { selected: false, filtered: filteredState }
    )
  }

  // clear stored feature
  selectedFeature = null

  // We only want a feature to be hovered, or selected, not both
  // If we find a hovered feature is what we are selecting, remove hovered features
  const selectedAndHovered = hoveredFeatures.find(
    (hovered) => hovered?.id === featureState?.id
  )
  if (selectedAndHovered) {
    setFeaturesStateHovered(map, [])
  }

  if (featureState?.id && featureState?.source) {
    const filteredState = featureIsFiltered(map, featureState)
    // Select feature, while retaining it's filtered state
    map.setFeatureState(
      {
        id: featureState.id,
        source: featureState.source,
        sourceLayer: featureState.sourceLayer,
      },
      { selected: true, filtered: filteredState }
    )

    // store new selected feature
    selectedFeature = featureState
  }
}

/**
 * Sets the selected_search state of a search feature with the given id,  and source
 * We want to use this function to highlight a given search feature, independently from
 * any other map features that may be selected.
 *
 * @param {Mapbox} map
 * @param {Object} searchFeature - {
 *    id: (string),
 *    source: (string)
 * }
 *
 */
export const setSearchFeatureSelected = (map, searchFeature) => {
  // unselect the previously selected search feature
  if (selectedSearchFeature) {
    map.setFeatureState(
      {
        id: selectedSearchFeature.id,
        source: selectedSearchFeature.source,
      },
      { selected_search: false }
    )
  }

  // clear stored feature
  selectedSearchFeature = null

  if (searchFeature?.id && searchFeature?.source) {
    // Select search feature
    map.setFeatureState(
      {
        id: searchFeature.id,
        source: searchFeature.source,
      },
      { selected_search: true }
    )

    // store new selected search feature
    selectedSearchFeature = searchFeature
  }
}

/**
 * Sets the feature state of the given id, source, and source layer to
 * the state passed in.  We want to use this function to highlight when
 * the feature is rendered on the map and we have the state object
 * setup in the stylesheet.  This function could also be used to set
 * other states of features assuming they are setup properly.
 *
 * @param {Mapbox} map
 * @param {Object} featureState - {
 *    id: (number| string),
 *    source: (string)
 *    sourceLayer: (string)
 *    state: (object e.g. { targeted: true})
 *
 * }
 *
 */
export const setFeatureTargeted = (map, featureState) => {
  // unselect the old feature
  if (targetedFeature) {
    // Features can be targeted & filtered simultaneously
    // Primarily when hovering items in the discover list to highlight on map
    const filteredState = featureIsFiltered(map, targetedFeature)
    // This unselects the previous feature, while retaining it's filtered state
    map.setFeatureState(
      {
        id: targetedFeature.id,
        source: targetedFeature.source,
        sourceLayer: targetedFeature.sourceLayer,
      },
      { targeted: false, filtered: filteredState }
    )
  }

  // clear stored feature
  targetedFeature = null

  // We only want a feature to be hovered, or targeted, not both
  // If we find a hovered feature is what we are targeting, remove hovered features
  const targetedAndHovered = hoveredFeatures.find(
    (hovered) => hovered?.id === featureState?.id
  )
  if (targetedAndHovered) {
    setFeaturesStateHovered(map, [])
  }

  if (featureState?.id && featureState?.source) {
    const filteredState = featureIsFiltered(map, featureState)
    // Target feature, while retaining it's filtered state
    map.setFeatureState(
      {
        id: featureState.id,
        source: featureState.source,
        sourceLayer: featureState.sourceLayer,
      },
      { targeted: true, filtered: filteredState }
    )

    // store new targeted feature
    targetedFeature = featureState
  }
}

/**
 * A nearly-identical copy of setFeatureSelected
 * This function takes an array of feature objects, and sets their feature state to `hovered`
 * @param {Mapbox} map
 * @param {Array} featureStates - Array of map feature objects
 *
 */
export const setFeaturesStateHovered = (map, featureStates) => {
  // unselect the old features
  if (hoveredFeatures.length) {
    hoveredFeatures.forEach((hoveredFeature) => {
      // Remove hover state from previous feature
      if (hoveredFeature.id !== selectedFeature?.id) {
        map.setFeatureState(
          {
            id: hoveredFeature.id,
            source: hoveredFeature.source,
            sourceLayer: hoveredFeature.sourceLayer,
          },
          { hovered: false }
        )
      }
    })
  }

  // clear stored feature
  hoveredFeatures = []
  if (featureStates.length) {
    featureStates.forEach((feat) => {
      // Ensure properties available, and that the feature also isn't selected
      if (
        feat?.id &&
        feat?.source &&
        feat?.id !== selectedFeature?.id &&
        feat?.id !== selectedSearchFeature?.id
      ) {
        // Set state for each feature passed in
        map.setFeatureState(
          {
            id: feat.id,
            source: feat.source,
            sourceLayer: feat.sourceLayer,
          },
          { hovered: true }
        )

        // store new selected feature
        hoveredFeatures = [...featureStates]
      }
    })
  }
}

/**
 * A nearly-identical copy of setFeatureSelected
 * This function takes an array of feature objects, and sets their feature state to `filtered`
 * @param {Mapbox} map
 * @param {Array} featureStates - Array of map feature objects
 *
 */
export const setFeaturesStateFiltered = (map, featureStates) => {
  // unselect the old features
  if (filteredFeatures.length) {
    filteredFeatures.forEach((filteredFeature) => {
      // Clear state of all features passed in
      map.removeFeatureState({
        id: filteredFeature.id,
        source: filteredFeature.source,
        sourceLayer: filteredFeature.sourceLayer,
      })
    })
  }

  // clear stored feature
  filteredFeatures = []

  featureStates.forEach((feat) => {
    // Ensure properties available, and that the feature also isn't selected
    if (feat.id && feat.source && feat.sourceLayer) {
      // Set state for each feature passed in
      map.setFeatureState(
        {
          id: feat.id,
          source: feat.source,
          sourceLayer: feat.sourceLayer,
        },
        { filtered: true }
      )

      // store new selected feature
      filteredFeatures = [...featureStates]
    }
  })
}

/**
 * A nearly-identical copy of setFeatureSelected
 * This function takes an array of feature objects, and sets their feature state to `favorited`
 * @param {Mapbox} map
 * @param {Array} featureStates - Array of map feature objects
 *
 */
export const setFeaturesStateFavorited = (map, featureStates) => {
  // unselect the old features
  if (favoritedFeatures.length) {
    favoritedFeatures.forEach((favoritedFeature) => {
      // Clear state of all features passed in
      map.removeFeatureState({
        id: favoritedFeature.id,
        source: favoritedFeature.source,
        sourceLayer: favoritedFeature.sourceLayer,
      })
    })
  }
  // clear stored feature
  favoritedFeatures = []
  featureStates.forEach((feat) => {
    // Ensure properties available
    if (feat.id && feat.source && feat.sourceLayer) {
      // Set state for each feature passed in
      map.setFeatureState(
        {
          id: feat.id,
          source: feat.source,
          sourceLayer: feat.sourceLayer,
        },
        { favorited: true }
      )

      // store new favorited features
      favoritedFeatures = [...featureStates]
    }
  })
}

/**
 * *(AI-generated)*
 * Places a user location marker on the map at the given coordinates.
 */
export const setUserMarker = (mapViewer, { longitude, latitude }) => {
  const eventEmitter = getMapEvents()
  if (!eventEmitter) {
    return
  }
  const element = document.createElement('div')
  element.classList.add('user-location-marker', 'center')

  if (userMarker) {
    userMarker.destroy()
  }

  userMarker = setHTMLElementToMapLocation(
    element,
    [longitude, latitude],
    mapViewer,
    eventEmitter
  )
}

/**
 * Sets the features in view state.
 *
 * This function queries all rendered features in the current viewport using the map-agnostic
 * mapViewer API. It queries features using screen coordinate bounds without a viewer specific tile buffer.
 *
 * @param {MapViewer} mapViewer - a MapViewer instance
 * @returns {void}
 */
export const setFeaturesInView = (mapViewer) => {
  let featuresInView = []
  try {
    const viewerCanvas = mapViewer.getCanvas()

    featuresInView = mapViewer.queryRenderedFeatures([
      0,
      viewerCanvas.height,
      viewerCanvas.width,
      0,
    ])
  } catch (e) {
    captureException(e, { tags: { map: 'query' } })
  }

  const outputFeatures = {}

  featuresInView.forEach((feature) => {
    const { source } = feature
    const mapFeature = MapFeature.fromMapbox(feature)

    if (!outputFeatures[source]) {
      outputFeatures[source] = []
    }

    outputFeatures[source].push(mapFeature)
  })

  setFeaturesInViewState(outputFeatures)
}

/**
 * *(AI-generated)*
 * Updates hovered feature state based on the current mouse event position.
 */
export const setFeaturesHovered = (map, e, useBaseCollisionBox = false) => {
  const featuresHovered = getMapFeatures(
    map,
    e.point,
    FEATURE_DETECTION_PADDING,
    useBaseCollisionBox
  )
  setFeaturesHoveredState(featuresHovered)
}

/**
 * *(AI-generated)*
 * Updates filtered feature state based on the current mouse event position.
 */
export const setFeaturesFiltered = (map, e) => {
  const featuresFiltered = getMapFeatures(
    map,
    e.point,
    FEATURE_DETECTION_PADDING
  )
  setFeaturesFilteredState(featuresFiltered)
}

/**
 * *(AI-generated)*
 * Updates favorited feature state based on the current mouse event position.
 */
export const setFeaturesFavorited = (map, e) => {
  const featuresFavorited = getMapFeatures(
    map,
    e.point,
    FEATURE_DETECTION_PADDING
  )
  setFeaturesFavoritedState(featuresFavorited)
}

/**
 * *(AI-generated)*
 * Queries map features at a geographic point and updates query feature state.
 */
export const setQueryFeatures = async (
  mapViewer,
  geoPoint,
  useBaseCollisionBox = false
  // eslint-disable-next-line require-await
) => {
  if (geoPoint === null) {
    setQueryFeaturesState([])
    return
  }

  if (mapViewer.isMoving() || !mapViewer.isReadyForQueries()) {
    getMapEvents()?.once('map-x-idle', () => {
      setQueryFeaturesEventHandler(mapViewer, geoPoint, useBaseCollisionBox)
    })
  } else {
    setQueryFeaturesEventHandler(mapViewer, geoPoint, useBaseCollisionBox)
  }
}

/**
 * Queries map features at a geographic location
 *
 * Converts geographic coordinates to screen pixels and queries rendered features.
 * Uses Map-X coordinate conversion abstraction for viewer independence.
 *
 * @param {MapViewer} mapViewer - MapViewer instance
 * @param {Object} geoPoint - Geographic point with longitude and latitude
 * @param {number} geoPoint.longitude - Longitude coordinate
 * @param {number} geoPoint.latitude - Latitude coordinate
 */
export const setQueryFeaturesEventHandler = (
  mapViewer,
  geoPoint,
  useBaseCollisionBox = false
) => {
  const { longitude, latitude } = geoPoint

  const [x, y] = mapViewer.convertLonLatToCtxPoint([longitude, latitude])
  const pixelCoord = { x, y }

  const featureArr = getMapFeatures(
    mapViewer.map,
    pixelCoord,
    FEATURE_DETECTION_PADDING,
    useBaseCollisionBox
  )
  setQueryFeaturesState(featureArr)
}

/**
 * Sets feature data.
 * @param {mapbox} map - Mapbox implementation
 * @param {string} sourceKey = key of source for new feature data
 * @param {object} features - array of geoJson features
 * @param {function} [callback] - Optional callback to execute after setting the features
 */
const updateFeatures = (map, sourceKey, features, emit, callback = null) => {
  const source = map.getSource(sourceKey)

  // verify source is found
  if (!source) {
    emit('map-event', {
      name: 'failed_get_source',
      type: 'performance',
      sourceKey: sourceKey,
      context: 'updating_features',
    })

    return
  }

  // update source data
  source.setData({
    type: 'FeatureCollection',
    features,
  })

  if (callback) {
    callback()
  }
}

/**
 * Removes and appends new popup to the map viewer
 * @param {*} mapViewer - MapViewer object
 * @param {*} popup - pop up properties and template
 */
export const setPopup = (mapViewer, popup) => {
  // remove existing popup
  if (mapViewerPopup) {
    mapViewerPopup.destroy()
  }

  // early return if provided null
  if (!popup) {
    return
  }

  const eventEmitter = getMapEvents()
  if (!eventEmitter) {
    return
  }

  const {
    template,
    component,
    coord,
    className,
    anchor = 'center',
    offset,
  } = popup

  if (component) {
    /**
     * Create HTMLElement from Vue app instance using MapElementWrapper
     * The `anchor-offset` class enables anchor-based positioning with optional offset
     * See MapElementWrapper component for detailed anchor/offset behavior
     */
    const element = createHTMLElement(MapElementWrapper, {
      vueAppInstance: component,
      className: `${className} anchor-offset`,
      anchor,
      offset,
    })

    /**
     * set the HTMLElement to map location and create a element controller
     */
    mapViewerPopup = setHTMLElementToMapLocation(
      element,
      [coord[0], coord[1]],
      mapViewer,
      eventEmitter
    )
  } else if (template) {
    const element = createHTMLElement(MapElementWrapper, {
      templateLiteral: template,
      className: `${className} anchor-offset`,
      anchor,
      offset,
    })

    /**
     * set the HTMLElement to map location and create a element controller
     */
    mapViewerPopup = setHTMLElementToMapLocation(
      element,
      [coord[0], coord[1]],
      mapViewer,
      eventEmitter
    )
  }
}

/**
 * Update marker state in mapbox with new markers list
 * @param {Object} mapViewer - a MapViewer instance
 * @param {Array} markers - new markers state
 */
export const updateMarkers = (mapViewer, markers) => {
  markersSaved = addMarkers(mapViewer, markers, markersSaved)
  markersSaved = removeMarkers(markers, markersSaved)
}

/**
 * Add markers that do not exist in storedMarkers array
 * @param {Object} mapViewer - a MapViewer instance
 * @param {Array} markers - new markers state
 * @param {Array} storedMarkers - stored markers state
 */
export const addMarkers = (mapViewer, markers, storedMarkers) => {
  const eventEmitter = getMapEvents()
  if (!eventEmitter) {
    return storedMarkers
  }
  // add markers to map
  const storedMarkersIds = storedMarkers.map((marker) => marker.id)
  const markersToAdd = markers.filter(
    // marker does not exist in saved markers
    (marker) => !storedMarkersIds.includes(marker.id)
  )

  markersToAdd.forEach((marker) => {
    const mapMarker = setHTMLElementToMapLocation(
      marker.element,
      marker.location,
      mapViewer,
      eventEmitter
    )

    // store marker for internal state
    storedMarkers.push({
      ...marker,
      mapMarker,
    })
  })

  return storedMarkers
}

/**
 * Remove stored markers that do not exist in markers array
 * @param {Array} markers - new markers state
 * @param {Array} storedMarkers - stored markers state
 */
export const removeMarkers = (markers, storedMarkers) => {
  // remove markers from map
  const markersIds = markers.map((marker) => marker.id)
  const markersToRemove = storedMarkers.filter(
    // saved marker does not exist in markers
    (marker) => !markersIds.includes(marker.id)
  )

  markersToRemove.forEach((marker) => {
    marker.mapMarker.destroy()

    const markerIndex = storedMarkers.find(
      (markerSaved) => markerSaved.id === marker.id
    )
    storedMarkers.splice(markerIndex, 1)
  })

  return storedMarkers
}

const featureIsFiltered = (map, featureState) => {
  const feature = map.getFeatureState({
    id: featureState.id,
    source: featureState.source,
    sourceLayer: featureState.sourceLayer,
  })

  return feature?.filtered ?? false
}
